import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Settings, Clock, Calendar, X, Plus, Save, Loader2,
  ChevronDown, ChevronRight, Copy, Check, Link
} from 'lucide-react';
import { toast } from 'sonner';

const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const ResaSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState('horaires');
  const [newBlockedDate, setNewBlockedDate] = useState('');
  const [widgetCopied, setWidgetCopied] = useState(false);

  useEffect(() => {
    api.get('/resa/settings').then(r => setSettings(r.data)).catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/resa/settings', settings);
      setSettings(res.data);
      toast.success('Parametres sauvegardes');
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const update = (key, val) => setSettings(s => ({ ...s, [key]: val }));

  const toggleDay = (list, day) => {
    const current = settings[list] || [];
    if (current.includes(day)) {
      update(list, current.filter(d => d !== day));
    } else {
      update(list, [...current, day]);
    }
  };

  const addBlockedDate = () => {
    if (!newBlockedDate) return;
    const current = settings.blocked_dates || [];
    if (!current.includes(newBlockedDate)) {
      update('blocked_dates', [...current, newBlockedDate].sort());
    }
    setNewBlockedDate('');
  };

  const removeBlockedDate = (date) => {
    update('blocked_dates', (settings.blocked_dates || []).filter(d => d !== date));
  };

  const updateDaySchedule = (day, field, value) => {
    const sched = { ...(settings.day_schedules || {}) };
    if (!sched[day]) sched[day] = {};
    sched[day][field] = value;
    update('day_schedules', sched);
  };

  const copyWidget = () => {
    const code = `<script src="${process.env.REACT_APP_BACKEND_URL}/api/widget/embed.js" data-button-text="Reserver une table" data-position="bottom-right"></script>`;
    navigator.clipboard.writeText(code);
    setWidgetCopied(true);
    setTimeout(() => setWidgetCopied(false), 2000);
  };

  if (loading || !settings) return (
    <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[rgb(var(--lm-gold))]" /></div>
  );

  const Section = ({ id, title, children }) => {
    const isOpen = openSection === id;
    return (
      <div className="lm-card overflow-hidden">
        <button onClick={() => setOpenSection(isOpen ? '' : id)}
          className="w-full flex items-center justify-between p-4 text-left"
          data-testid={`section-${id}`}>
          <span className="text-sm font-title text-[rgb(var(--lm-text))]">{title}</span>
          {isOpen ? <ChevronDown size={14} className="text-[rgb(var(--lm-text-muted))]" /> : <ChevronRight size={14} className="text-[rgb(var(--lm-text-muted))]" />}
        </button>
        {isOpen && <div className="px-4 pb-4 space-y-3 border-t border-[rgb(var(--lm-border)/0.3)]">{children}</div>}
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid="resa-settings-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings size={18} className="text-[rgb(var(--lm-gold))]" />
          <h1 className="text-lg font-title text-[rgb(var(--lm-text))]">Parametres reservations</h1>
        </div>
        <Button onClick={handleSave} disabled={saving} size="sm"
          className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 gap-1" data-testid="save-settings">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Sauvegarder
        </Button>
      </div>

      {/* Info */}
      <Section id="info" title="Informations du restaurant">
        <div className="pt-3 space-y-2">
          <div>
            <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Nom</label>
            <Input value={settings.restaurant_name || ''} onChange={e => update('restaurant_name', e.target.value)}
              className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="setting-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Telephone</label>
              <Input value={settings.phone || ''} onChange={e => update('phone', e.target.value)}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="setting-phone" />
            </div>
            <div>
              <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Sous-titre</label>
              <Input value={settings.hero_subtitle || ''} onChange={e => update('hero_subtitle', e.target.value)}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="setting-subtitle" />
            </div>
          </div>
          <div>
            <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Adresse</label>
            <Input value={settings.address || ''} onChange={e => update('address', e.target.value)}
              className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="setting-address" />
          </div>
        </div>
      </Section>

      {/* Horaires */}
      <Section id="horaires" title="Horaires et creneaux">
        <div className="pt-3 space-y-4">
          {/* Service toggles */}
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={settings.lunch_enabled} onChange={e => update('lunch_enabled', e.target.checked)}
                className="rounded" data-testid="toggle-lunch" />
              <span className="text-xs text-[rgb(var(--lm-text-secondary))]">Service midi</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={settings.dinner_enabled} onChange={e => update('dinner_enabled', e.target.checked)}
                className="rounded" data-testid="toggle-dinner" />
              <span className="text-xs text-[rgb(var(--lm-text-secondary))]">Service soir</span>
            </label>
          </div>

          {/* Default hours */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-[rgb(var(--lm-bg))] rounded-sm">
              <p className="text-[9px] text-amber-400 uppercase tracking-wider mb-2">Midi</p>
              <div className="grid grid-cols-2 gap-2">
                <Input type="time" value={settings.lunch_start} onChange={e => update('lunch_start', e.target.value)}
                  className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-xs" data-testid="lunch-start" />
                <Input type="time" value={settings.lunch_end} onChange={e => update('lunch_end', e.target.value)}
                  className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-xs" data-testid="lunch-end" />
              </div>
            </div>
            <div className="p-3 bg-[rgb(var(--lm-bg))] rounded-sm">
              <p className="text-[9px] text-indigo-400 uppercase tracking-wider mb-2">Soir</p>
              <div className="grid grid-cols-2 gap-2">
                <Input type="time" value={settings.dinner_start} onChange={e => update('dinner_start', e.target.value)}
                  className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-xs" data-testid="dinner-start" />
                <Input type="time" value={settings.dinner_end} onChange={e => update('dinner_end', e.target.value)}
                  className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))] text-xs" data-testid="dinner-end" />
              </div>
            </div>
          </div>

          {/* Slot config */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Duree creneau</label>
              <select value={settings.slot_duration} onChange={e => update('slot_duration', parseInt(e.target.value))}
                className="w-full h-9 px-2 rounded-sm bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-xs text-[rgb(var(--lm-text))]"
                data-testid="slot-duration">
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={60}>60 min</option>
              </select>
            </div>
            <div>
              <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Max resa/creneau</label>
              <Input type="number" min={1} value={settings.max_reservations_per_slot}
                onChange={e => update('max_reservations_per_slot', parseInt(e.target.value) || 1)}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-xs" data-testid="max-resa" />
            </div>
            <div>
              <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Max couverts/creneau</label>
              <Input type="number" min={1} value={settings.max_covers_per_slot}
                onChange={e => update('max_covers_per_slot', parseInt(e.target.value) || 1)}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] text-xs" data-testid="max-covers" />
            </div>
          </div>
        </div>
      </Section>

      {/* Jours de fermeture */}
      <Section id="fermeture" title="Jours de fermeture">
        <div className="pt-3">
          <div className="space-y-1">
            {JOURS.map((jour, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-[rgb(var(--lm-border)/0.2)] last:border-0">
                <span className="text-xs text-[rgb(var(--lm-text))]">{jour}</span>
                <div className="flex gap-3">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={(settings.closed_days || []).includes(i)}
                      onChange={() => toggleDay('closed_days', i)} className="rounded" />
                    <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">Ferme</span>
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={(settings.lunch_closed_days || []).includes(i)}
                      onChange={() => toggleDay('lunch_closed_days', i)} className="rounded" />
                    <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">Midi</span>
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={(settings.dinner_closed_days || []).includes(i)}
                      onChange={() => toggleDay('dinner_closed_days', i)} className="rounded" />
                    <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">Soir</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Dates bloquees */}
      <Section id="blocked" title="Dates bloquees">
        <div className="pt-3 space-y-2">
          <div className="flex gap-2">
            <Input type="date" value={newBlockedDate} onChange={e => setNewBlockedDate(e.target.value)}
              className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] flex-1" data-testid="blocked-date-input" />
            <Button onClick={addBlockedDate} size="sm" className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))]" data-testid="add-blocked-date">
              <Plus size={14} />
            </Button>
          </div>
          <div className="flex flex-wrap gap-1">
            {(settings.blocked_dates || []).map(d => (
              <span key={d} className="flex items-center gap-1 px-2 py-1 bg-[rgb(var(--lm-bg))] rounded-sm text-[10px] text-[rgb(var(--lm-text-secondary))]">
                {d}
                <button onClick={() => removeBlockedDate(d)} className="text-[rgb(var(--lm-red))] hover:text-red-400">
                  <X size={10} />
                </button>
              </span>
            ))}
          </div>
        </div>
      </Section>

      {/* Options client */}
      <Section id="options" title="Options client">
        <div className="pt-3 space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={settings.show_zone_selection}
              onChange={e => update('show_zone_selection', e.target.checked)} className="rounded" data-testid="toggle-zones" />
            <span className="text-xs text-[rgb(var(--lm-text-secondary))]">Afficher la selection de zone</span>
          </label>
          <div>
            <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">CGV / Conditions</label>
            <textarea value={settings.terms_text || ''} onChange={e => update('terms_text', e.target.value)}
              className="w-full h-16 px-3 py-2 rounded-sm bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-xs text-[rgb(var(--lm-text))] resize-none"
              placeholder="En reservant, vous acceptez..." data-testid="terms-text" />
          </div>
        </div>
      </Section>

      {/* Avis Google & TripAdvisor */}
      <Section id="avis" title="Emails automatiques & Avis">
        <div className="pt-3 space-y-4">
          {/* Toggles */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={settings.reminder_enabled !== false}
                onChange={e => update('reminder_enabled', e.target.checked)} className="rounded" data-testid="toggle-reminder" />
              <span className="text-xs text-[rgb(var(--lm-text-secondary))]">Rappel 24h avant (email automatique)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={settings.review_enabled !== false}
                onChange={e => update('review_enabled', e.target.checked)} className="rounded" data-testid="toggle-review" />
              <span className="text-xs text-[rgb(var(--lm-text-secondary))]">Remerciement + avis apres la visite</span>
            </label>
          </div>

          {/* URLs */}
          <div>
            <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Lien avis Google</label>
            <div className="flex gap-2">
              <Input value={settings.google_review_url || ''} onChange={e => update('google_review_url', e.target.value)}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] flex-1" placeholder="https://g.page/r/..."
                data-testid="google-review-url" />
              <Button size="sm" variant="outline" className="border-[rgb(var(--lm-border))] shrink-0">
                <Link size={12} />
              </Button>
            </div>
          </div>
          <div>
            <label className="text-[9px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Lien avis TripAdvisor</label>
            <div className="flex gap-2">
              <Input value={settings.tripadvisor_url || ''} onChange={e => update('tripadvisor_url', e.target.value)}
                className="bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] flex-1" placeholder="https://www.tripadvisor.fr/..."
                data-testid="tripadvisor-url" />
              <Button size="sm" variant="outline" className="border-[rgb(var(--lm-border))] shrink-0">
                <Link size={12} />
              </Button>
            </div>
          </div>

          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] leading-relaxed">
            Le rappel est envoye 24h avant la reservation. L'email de remerciement avec les liens d'avis est envoye le lendemain de la visite.
          </p>
        </div>
      </Section>

      {/* Widget */}
      <Section id="widget" title="Widget embarquable">
        <div className="pt-3 space-y-2">
          <p className="text-xs text-[rgb(var(--lm-text-muted))]">
            Copiez ce code et collez-le dans votre site web pour afficher un bouton de reservation.
          </p>
          <div className="bg-[rgb(var(--lm-bg))] p-3 rounded-sm">
            <code className="text-[10px] text-[rgb(var(--lm-gold))] break-all">
              {`<script src="${process.env.REACT_APP_BACKEND_URL}/api/widget/embed.js" data-button-text="Reserver" data-position="bottom-right"></script>`}
            </code>
          </div>
          <Button onClick={copyWidget} size="sm" variant="outline" className="border-[rgb(var(--lm-border))] gap-1" data-testid="copy-widget">
            {widgetCopied ? <Check size={12} /> : <Copy size={12} />}
            {widgetCopied ? 'Copie !' : 'Copier le code'}
          </Button>
          <p className="text-xs text-[rgb(var(--lm-text-muted))]">
            Lien direct : <a href={`${window.location.origin}/reservation`} target="_blank" rel="noreferrer"
              className="text-[rgb(var(--lm-gold))] underline">{window.location.origin}/reservation</a>
          </p>
        </div>
      </Section>
    </div>
  );
};

export default ResaSettings;
